import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AdminLayout from '../../components/AdminLayout';
import { getFAQs, createFAQ, updateFAQ, deleteFAQ, reorderFAQs } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const SortableFAQItem = ({ faq, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: faq.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="admin-card"
      data-testid={`faq-item-${faq.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex gap-4 flex-1">
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing mt-1 flex-shrink-0"
            data-testid={`drag-handle-faq-${faq.id}`}
          >
            <GripVertical className="w-5 h-5 text-neutral-300 hover:text-neutral-500" />
          </button>
          <div className="flex-1">
            <h3 className="font-semibold text-lg mb-2">{faq.question}</h3>
            <p className="text-neutral-500 whitespace-pre-wrap">{faq.answer}</p>
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <Button 
            variant="outline" 
            size="icon"
            onClick={() => onEdit(faq)}
            data-testid={`edit-faq-${faq.id}`}
          >
            <Pencil className="w-4 h-4" />
          </Button>
          <Button 
            variant="outline" 
            size="icon"
            className="text-red-600 hover:text-red-700"
            onClick={() => onDelete(faq)}
            data-testid={`delete-faq-${faq.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const AdminFAQs = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedFAQ, setSelectedFAQ] = useState(null);
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    order: 0
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      const res = await getFAQs();
      setFaqs(res.data);
    } catch (error) {
      toast.error('Failed to load FAQs');
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      const oldIndex = faqs.findIndex((f) => f.id === active.id);
      const newIndex = faqs.findIndex((f) => f.id === over.id);
      
      const newFaqs = arrayMove(faqs, oldIndex, newIndex);
      setFaqs(newFaqs);
      
      // Update order in backend
      const reorderData = newFaqs.map((faq, index) => ({
        id: faq.id,
        order: index
      }));
      
      try {
        await reorderFAQs(reorderData);
        toast.success('Order updated');
      } catch (error) {
        toast.error('Failed to update order');
        fetchFAQs(); // Revert on error
      }
    }
  };

  const handleOpenDialog = (faq = null) => {
    if (faq) {
      setFormData({
        question: faq.question,
        answer: faq.answer,
        order: faq.order
      });
      setSelectedFAQ(faq);
    } else {
      setFormData({
        question: '',
        answer: '',
        order: faqs.length
      });
      setSelectedFAQ(null);
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (selectedFAQ) {
        await updateFAQ(selectedFAQ.id, formData);
        toast.success('FAQ updated successfully');
      } else {
        await createFAQ(formData);
        toast.success('FAQ created successfully');
      }
      setDialogOpen(false);
      fetchFAQs();
    } catch (error) {
      toast.error('Failed to save FAQ');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteFAQ(selectedFAQ.id);
      toast.success('FAQ deleted successfully');
      setDeleteDialogOpen(false);
      fetchFAQs();
    } catch (error) {
      toast.error('Failed to delete FAQ');
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-faqs-page">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
              FAQs
            </h1>
            <p className="text-neutral-500 mt-2">Manage frequently asked questions (drag to reorder)</p>
          </div>
          <Button 
            onClick={() => handleOpenDialog()}
            className="bg-neutral-900 hover:bg-neutral-800"
            data-testid="add-faq-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add FAQ
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-900"></div>
          </div>
        ) : faqs.length === 0 ? (
          <div className="admin-card text-center py-16">
            <p className="text-neutral-500 mb-4">No FAQs yet</p>
            <Button onClick={() => handleOpenDialog()}>Create your first FAQ</Button>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={faqs.map(f => f.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-4">
                {faqs.map((faq) => (
                  <SortableFAQItem
                    key={faq.id}
                    faq={faq}
                    onEdit={handleOpenDialog}
                    onDelete={(faq) => {
                      setSelectedFAQ(faq);
                      setDeleteDialogOpen(true);
                    }}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}

        {/* FAQ Form Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {selectedFAQ ? 'Edit FAQ' : 'Add FAQ'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="question">Question</Label>
                <Input
                  id="question"
                  value={formData.question}
                  onChange={(e) => setFormData(prev => ({ ...prev, question: e.target.value }))}
                  placeholder="e.g., How far in advance should I book?"
                  required
                  data-testid="faq-question-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="answer">Answer</Label>
                <Textarea
                  id="answer"
                  value={formData.answer}
                  onChange={(e) => setFormData(prev => ({ ...prev, answer: e.target.value }))}
                  placeholder="Provide a detailed answer..."
                  rows={5}
                  required
                  data-testid="faq-answer-input"
                />
              </div>

              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-neutral-900 hover:bg-neutral-800" data-testid="save-faq-btn">
                  {selectedFAQ ? 'Update FAQ' : 'Create FAQ'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete FAQ</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this FAQ? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-faq-btn"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default AdminFAQs;
